import { HttpClient } from "@angular/common/http";
import { environment as env } from "../../../../../environments/environment.prod";
import { Injectable } from "@angular/core";
import { AbstractService } from "app/services/abstract.service";
import { Observable } from "rxjs";
import { IAPIListResult, IPagination, IPaginationResult, IRequestResult } from "app/common/interfaces";
import { andWhere, buildQuery, CombinedFilter, FilterFn, noop, Ops, orderBy, OrderVariant, orWhere, page, select, where } from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import ReturnTypeAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/masters/return-type/return-type.entity";
import { ICreateChangeConditionType, IUpdateChangeConditionType } from "./interfaces/change-condition.interface";
import { IFilters } from "../../filters/interfaces/filterInterface";

const apiUrl = env.API_AVE_RETURN;
const moduleName = "return-type";

@Injectable({
  providedIn: "root",
})
export class ChangeConditionService extends AbstractService<ReturnTypeAttributes, ICreateChangeConditionType, IUpdateChangeConditionType> {

    constructor(
        http: HttpClient,
    ) {
        super(apiUrl, moduleName, http);
    }

    /**
     * Obtiene condiciones de cambio con paginación y filtros.
     *
     * Estrategia de búsqueda:
     * - Sin búsqueda: Un solo objeto con filtros base
     * - Con búsqueda: Dos objetos (OR lógico) - busca en 'code', 'name'
     *
     * Cada objeto del OR debe incluir TODAS las condiciones base para mantener consistencia.
     *
     * @param options Pagination options
     * @param filter Filters to apply
     * @returns Data list
     *
     * Search fields: code, name
     * Array filters: changeConditionTypeId, status
     * Single filters: changeConditionClassId, companyId
     */
    getAllPaginateReturnType(
        options: IPagination<ReturnTypeAttributes>,
        filter: IFilters
    ): Observable<IPaginationResult<ReturnTypeAttributes>> {

        const hasSearch = !!options.search;

        /**
         * Construye las condiciones base que SIEMPRE deben aplicarse.
         * Incluye: _deleted=false, filtros de clase, tipo, estado, compañía.
         *
         * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
         */
        const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<ReturnTypeAttributes>[] => {
            const deletedFilter: FilterFn<ReturnTypeAttributes> = params.useOrWhere
                ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
                : where('_deleted', Ops.eq(false));   // Primer objeto del array

            const filters: FilterFn<ReturnTypeAttributes>[] = [deletedFilter];

            if (filter?.changeConditionClassId) {
                filters.push(andWhere('class' as keyof ReturnTypeAttributes, Ops.eq(filter.changeConditionClassId)));
            }

            if (filter?.changeConditionTypeId && filter.changeConditionTypeId.length > 0) {
                filters.push(andWhere('type' as keyof ReturnTypeAttributes, Ops.in(...filter.changeConditionTypeId)));
            }

            if (filter?.status && filter.status.length > 0 && !filter.status.includes(null)) {
                filters.push(andWhere('statusId', Ops.in(...filter.status)));
            }

            if (filter?.companyId) {
                filters.push(andWhere('company.id' as keyof ReturnTypeAttributes, Ops.eq(filter.companyId)));
            }

            return filters;
        };

        // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

        // Objeto 1: Condiciones base + búsqueda por CODE (o solo condiciones si no hay búsqueda)
        const searchByCodeQueryObject: FilterFn<ReturnTypeAttributes>[] = [
            ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
            hasSearch ? andWhere("code", Ops.like(options.search!, "i")) : noop()
        ];

        // Objeto 2: Condiciones base + búsqueda por NAME (solo si hay búsqueda)
        const searchByNameQueryObject: FilterFn<ReturnTypeAttributes>[] = hasSearch ? [
            ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto)
            andWhere("name", Ops.like(options.search!, "i"))
        ] : [];

        // ========== ENSAMBLAJE FINAL ==========

        const query = buildQuery<ReturnTypeAttributes>(
            // Expandimos ambos objetos en el buildQuery
            ...searchByCodeQueryObject,
            ...searchByNameQueryObject,

            // Ordenamiento y paginación
            orderBy(
                options.sort ?? "name",
                options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
            ),
            page(options.currentPage ?? 1, options.limitPerPage ?? 100)
        );

        const API_URL: string = apiUrl +
            `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
        return this.http.get<IPaginationResult<ReturnTypeAttributes>>(API_URL);
    }

    /**
     * Find to combo box
     * @returns Data
    */
    findToComboBox(type?: string): Observable<IRequestResult<ReturnTypeAttributes[]>> {
        const query = buildQuery<ReturnTypeAttributes>(
            where("statusId", StatusEnum.ACTIVE),
            andWhere("class", Ops.eq(type)),
            select(["_id","code", "name"]),
            orderBy("code", OrderVariant.Asc)
        );

        const API_URL: string =
            apiUrl +
            `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
        return this.http.get<IRequestResult<ReturnTypeAttributes[]>>(API_URL);
    }
}
